import {ChangeDetectorRef, Component, OnDestroy, OnInit, VERSION} from "@angular/core";
import {Compbaser} from "../../components/compbaser";
import {AccessService, LocalStoreService, StoreSelectorsService} from "../../services";
import {EnumConnectionType, EnumStudioType, IAppStatic, IUser} from "../../interfaces";
import {Router} from "@angular/router";
import {map, take} from "rxjs/operators";
import {compareIds, DEFAULT_PORT, isMainTouchScene, isNullOrUndefOrEmpty, isSceneEmbedded, WEB_SERVER} from "../../common";
import {actionSelectedSceneId, actionSelectedSceneTouchId} from "../../store/actions/user.actions";
import {TouchLib} from "../editor/touch/touchLib";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import {Lib} from "../../../libs/Lib";

const {version: appVersion} = require('../../../../package.json');
const {cloudVersion: cloudVersion} = require('../../../../package.json');

/** localStorage key the scenes page reads on mount. When set to "1", the
 *  scenes page consumes the flag (removes it) and opens the same
 *  "pick scene type" dialog the in-page "create scene" button opens.
 *  Wired here so any home-hero action that should bring the user to
 *  the create dialog can use it: the create button + the blank-scene chip.
 *  The filter draft uses the existing `sceneFilter` localStorage key
 *  the scene-list-details component already loads at startup. */
export const HOME_OPEN_CREATE_SCENE_KEY = "home.openCreateSceneOnLoad";

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent extends Compbaser implements OnInit, OnDestroy {

    protected readonly faEdit = faEdit;
    protected readonly EnumStudioType = EnumStudioType;
    studioType: EnumStudioType;
    enterpriseMode: boolean;
    dashboardMode: boolean;
    /** Whether to show the "getting started" / download-player section.
     *  Hidden for accounts under a reseller (enterprise white-label) so
     *  we don't push MediaSignage download/branding into a reseller's
     *  customer experience. Derived from Lib.GetAccountVersion: only
     *  'FREE' and 'PLUS' tiers see the onboarding card. */
    showOnboardingSection = false;
    uiMode;
    appVersion = appVersion;
    cloudVersion = cloudVersion;
    version = VERSION.full;
    embedCustomHomePage;
    user: IUser;
    appSettings: IAppStatic;

    /* ── Hero band state (uiMode 3 only) ───────────────────────────── */
    /** Filter draft — typed text drives the live scene autocomplete
     *  AND, on submit, pre-applies to the scenes-page list filter via
     *  the existing `sceneFilter` localStorage key. Same name kept
     *  (aiPrompt) to avoid a wider template diff; semantically it's
     *  now a scene-list filter. */
    aiPrompt = "";
    greeting = "welcome";
    userFirstName = "";
    stationsTotal = 0;
    stationsOnline = 0;
    scenesTotal = 0;

    /** All user scenes — drives the autocomplete dropdown under the
     *  hero filter input. Subscribed once via getScenes(); same data
     *  the scenes page uses, no extra API traffic. */
    private allScenes: any[] = [];
    /** Top N matches for the current filter text. Recomputed via
     *  recomputeHeroMatches() on every input change, not as a getter
     *  (OnPush would otherwise re-run it on every CD cycle). */
    heroMatches: Array<{id: string; label: string; size: string}> = [];
    /** Dropdown visibility — true when input has focus AND there's
     *  text AND there are matches. */
    isHeroFilterFocused = false;
    /** Keyboard-highlight index for arrow-up/down navigation. */
    heroMatchHighlight = -1;
    private readonly HERO_MATCH_LIMIT = 8;
    /** Blur close timer — debounced so clicks on dropdown items land
     *  before we hide the panel. */
    private heroFilterBlurTimer: any = null;

    constructor(private router: Router, private storeSelector: StoreSelectorsService, private accessService: AccessService, private cdr: ChangeDetectorRef, private localStoreService: LocalStoreService) {
        super();
        this.setUiMode(0);
        this.cod(
            storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                })
        )
        this.cod(
            this.storeSelector.getUser().subscribe((i_user) => {
                this.user = i_user;
                this.checkEmbeddedCustomHomePage();
            })
        );
    }

    ngOnInit(): void {
        this.cod(
            this.storeSelector.getUser().subscribe((i_user) => {
                this.user = i_user;
                this.studioType = i_user.studioType;
                this.enterpriseMode = i_user.enterprise;
                this.dashboardMode = i_user.dashboard;
                // Onboarding/download card is FREE+PLUS only. Hides for
                // enterprise users under a reseller to keep reseller
                // branding clean (no MediaSignage download flow surfaced).
                const ver = Lib.GetAccountVersion(i_user);
                this.showOnboardingSection = ver === "FREE" || ver === "PLUS";
                this.userFirstName = this.deriveFirstName(i_user);
                this.greeting = this.timeOfDayGreeting();
                if (this.dashboardMode) {
                    this.uiMode = 1;
                    this.router.navigateByUrl('/statistics');
                } else if (this.studioType === EnumStudioType.LITE || this.studioType === EnumStudioType.PRO) {
                    this.setUiMode(2);
                } else {
                    this.setUiMode(3);
                }
            })
        );

        // Live KPI strip — count stations online + total scenes. Cheap
        // observers that piggy-back on the same store data the rest of
        // the app already polls; no extra API traffic.
        this.cod(
            this.storeSelector.getStationsComplete(EnumConnectionType.ALL).subscribe((stations) => {
                const list = stations || [];
                this.stationsTotal = list.length;
                this.stationsOnline = list.filter((s) => (s as any)?.stat?.$?.connection === "1").length;
                this.cdr.markForCheck();
            }),
        );
        this.cod(
            this.storeSelector.getScenes().subscribe((scenes) => {
                this.allScenes = scenes || [];
                this.scenesTotal = this.allScenes.length;
                // If the user is mid-typing when scenes load (cold-start
                // race), refresh the dropdown so it doesn't stay empty.
                if (this.aiPrompt) this.recomputeHeroMatches();
                this.cdr.markForCheck();
            }),
        );
    }

    /** Pick a friendly first-name from whichever user field is populated. */
    private deriveFirstName(u: IUser): string {
        if (!u) return "";
        const candidates = [(u as any)?.name, (u as any)?.firstName, u?.username, u?.email];
        for (const c of candidates) {
            if (typeof c !== "string") continue;
            const trimmed = c?.trim();
            if (!trimmed) continue;
            // For email, take the local part before @
            const local = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
            // Take the first whitespace-delimited word and strip non-letters
            const first = local.split(/[\s._-]+/)[0]?.replace(/[^a-zA-Z]/g, "") ?? "";
            if (first.length >= 2) {
                return first.charAt(0).toLowerCase() + first.slice(1).toLowerCase();
            }
        }
        return "";
    }

    /** "good morning / afternoon / evening" — local-clock based, no i18n
     *  branching; copy is alphanumeric+spaces per text-safety policy. */
    private timeOfDayGreeting(): string {
        const h = new Date().getHours();
        if (h < 12) return "good morning";
        if (h < 18) return "good afternoon";
        return "good evening";
    }

    /** "create scene" button click — same effect as clicking
     *  "create scene" on the scenes page: route to /editor/list and
     *  open the pick-scene-type dialog. Any text typed into the filter
     *  is also forwarded so the scene list opens pre-filtered.
     *  AI generation gates inside that dialog stay enforced — this
     *  surface intentionally no longer pretends AI is free. */
    onHeroCreateSceneSubmit(): void {
        this.forwardFilterIfAny();
        try {
            window.localStorage.setItem(HOME_OPEN_CREATE_SCENE_KEY, "1");
        } catch { /* quota — ignore */ }
        this.router.navigateByUrl("/editor/list");
    }

    /** Filter input → Enter. Forwards the typed text and routes to
     *  the scene list WITHOUT opening the create dialog — the user
     *  just wants to see the filtered list. */
    onHeroFilterEnter(): void {
        this.forwardFilterIfAny();
        this.router.navigateByUrl("/editor/list");
    }

    onHeroChipBlankScene(): void {
        // Same path as the main button — let the user pick "new empty
        // scene" inside the standard create dialog so the flow stays
        // consistent with how power users do it from the scenes page.
        this.onHeroCreateSceneSubmit();
    }

    onHeroChipBrowseTemplates(): void {
        // Templates render below the hero in the same view — scroll to them.
        const el = document.querySelector("app-home-templates");
        if (el) {
            (el as HTMLElement).scrollIntoView({behavior: "smooth", block: "start"});
        }
    }

    onHeroChipPlayers(): void {
        this.router.navigateByUrl("/stations");
    }

    onHeroChipResources(): void {
        this.router.navigateByUrl("/resources/list");
    }

    /** Persist the typed filter into the same localStorage key the scenes
     *  page reads on init (`sceneFilter`). Only writes when the user
     *  actually typed something so we never clobber an existing scenes
     *  filter on an empty submit. */
    private forwardFilterIfAny(): void {
        const text = (this.aiPrompt || "").trim();
        if (!text) return;
        // sceneFilter lives inside the ILocalStorageConfig blob, not as a
        // top-level localStorage key — go through the wrapper or scenes
        // page won't see it (sync=false: client-only, no need to roundtrip
        // through the server).
        this.localStoreService.setStorageValConfig("sceneFilter", text, false);
    }

    /* ── Hero autocomplete dropdown ────────────────────────────────── */

    /** ngModelChange handler for the input — recomputes the dropdown. */
    onHeroFilterInput(): void {
        this.recomputeHeroMatches();
        this.heroMatchHighlight = this.heroMatches.length > 0 ? 0 : -1;
    }

    onHeroFilterFocus(): void {
        if (this.heroFilterBlurTimer) {
            clearTimeout(this.heroFilterBlurTimer);
            this.heroFilterBlurTimer = null;
        }
        this.isHeroFilterFocused = true;
        if (this.aiPrompt) {
            this.recomputeHeroMatches();
            this.heroMatchHighlight = this.heroMatches.length > 0 ? 0 : -1;
        }
    }

    onHeroFilterBlur(): void {
        // Defer the close so a click on a dropdown item lands first.
        this.heroFilterBlurTimer = setTimeout(() => {
            this.isHeroFilterFocused = false;
            this.heroMatchHighlight = -1;
            this.cdr.markForCheck();
        }, 180);
    }

    /** Arrow-up / arrow-down on the input — navigate the dropdown. */
    onHeroFilterArrow(direction: -1 | 1, ev: Event): void {
        if (!this.isHeroDropdownVisible) return;
        ev.preventDefault();
        const len = this.heroMatches.length;
        if (len === 0) {
            this.heroMatchHighlight = -1;
            return;
        }
        if (this.heroMatchHighlight < 0) {
            this.heroMatchHighlight = direction === 1 ? 0 : len - 1;
        } else {
            this.heroMatchHighlight = (this.heroMatchHighlight + direction + len) % len;
        }
    }

    /** Pick a scene from the dropdown — jump straight into the
     *  scene-builder for that scene (or the touch editor for slider
     *  wrappers), mirroring how scene-list-details.onSelectedRow opens
     *  a scene. Falls back to the filtered scene list only if the
     *  scene can't be opened directly (gone from store, embedded). */
    onHeroFilterPick(match: {id: string; label: string; size: string}): void {
        this.aiPrompt = match.label;
        this.isHeroFilterFocused = false;
        this.heroMatchHighlight = -1;
        const scene = this.allScenes.find((s) => String(s?.Player?.$?.id ?? "") === match.id);
        if (!scene || isSceneEmbedded(scene)) {
            this.localStoreService.setStorageValConfig("sceneFilter", match.label, false);
            this.router.navigateByUrl("/editor/list");
            return;
        }
        if (isMainTouchScene(scene)) {
            const firstChildSceneId = TouchLib.getFirstSlideSceneId(scene);
            this.storeSelector.dispatch(actionSelectedSceneId({id: firstChildSceneId}));
            this.storeSelector.dispatch(actionSelectedSceneTouchId(TouchLib.createSelectedSliderPayload(scene.Player.$.id, scene.Player.$.label, firstChildSceneId)));
            this.router.navigateByUrl(`/editor/touch/scene/${scene.Player.$.id}`);
        } else {
            this.storeSelector.dispatch(actionSelectedSceneId({id: scene.Player.$.id}));
            this.router.navigateByUrl(`/editor/scene/${scene.Player.$.id}`);
        }
    }

    /** Enter override — if the dropdown has a highlight, pick that;
     *  otherwise fall through to the plain "submit filter" route. */
    onHeroFilterEnterOrPick(): void {
        if (this.isHeroDropdownVisible && this.heroMatchHighlight >= 0) {
            this.onHeroFilterPick(this.heroMatches[this.heroMatchHighlight]);
            return;
        }
        this.onHeroFilterEnter();
    }

    /** Convenience getter for the template — keeps the @if expression
     *  short and ensures the dropdown only shows when meaningful. */
    get isHeroDropdownVisible(): boolean {
        return this.isHeroFilterFocused
            && (this.aiPrompt || "").trim().length > 0
            && this.heroMatches.length > 0;
    }

    /** Pull top N matches by case-insensitive substring on Player.$.label. */
    private recomputeHeroMatches(): void {
        const q = (this.aiPrompt || "").trim().toLowerCase();
        if (!q) {
            this.heroMatches = [];
            return;
        }
        const results: Array<{id: string; label: string; size: string}> = [];
        for (const s of this.allScenes) {
            const p = s?.Player?.$;
            if (!p) continue;
            const label = String(p.label || "").trim();
            if (!label) continue;
            if (label.toLowerCase().indexOf(q) === -1) continue;
            const layout = s?.Player?.Data?.[0]?.Layout?.[0]?.$;
            const w = layout?.width;
            const h = layout?.height;
            const size = w && h ? `${w}x${h}` : "";
            results.push({id: String(p.id ?? ""), label, size});
            if (results.length >= this.HERO_MATCH_LIMIT) break;
        }
        this.heroMatches = results;
    }

    private setUiMode(v) {
        /** never override 4, custom HTML enterprise home page **/
        // if (this.uiMode === 4) return;
        // console.log('loading home mode ' + v);
        this.uiMode = v;
    }

    /** if enterprise user check if we need to swap overview page to a custom home page **/
    private checkEmbeddedCustomHomePage() {
        if (!this.user.enterprise) return;
        this.cod(
            this.accessService.getGroupsCollection("SignStudio Dashboard").pipe(map((value) => value))
                .pipe(take(1),
                    map((value) => {
                        return value?.$?.showCustomHome ? value.$.showCustomHome : '0';
                    })
                ).subscribe((v) => {
                if (compareIds(v, '1') && !isNullOrUndefOrEmpty(this.user.customStudio?.Studio?.Application?.[0]?.Links?.[0]?.$?.download)) {
                    if (this.user.customStudio.Studio.Application[0].Links[0].$.download.indexOf('http') === -1) return;
                    const b64 = btoa(this.user.customStudio.Studio.Application[0].Links[0].$.download);
                    this.embedCustomHomePage = `${WEB_SERVER}:${DEFAULT_PORT}/proxyRequest/${b64}`;
                    if (this.embedCustomHomePage) {
                        this.setUiMode(4);
                    }
                }
            })
        );
    }


    download(value) {
        window.location.replace(this.appSettings.studioLiteLegacy);

    }
}


